'use client'

import { useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

export function useInstallPWA() {
  const [ready, setReady] = useState(true)
  const [installed, setInstalled] = useState(false)
  const [supported, setSupported] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)

  // Browser detection states
  const [needSafari, setNeedSafari] = useState(false)
  const [needIOS, setNeedIOS] = useState(false)
  const [needIpadOS, setNeedIpadOS] = useState(false)
  const [needChrome, setNeedChrome] = useState(false)

  useEffect(() => {
    // Check if PWA is already installed using multiple methods
    const checkInstalled = () => {
      if ('matchMedia' in window) {
        // Check standalone mode
        const isStandalone =
          window.matchMedia('(display-mode: standalone)').matches ||
          ('standalone' in window.navigator && Boolean(window.navigator.standalone) === true) ||
          document.referrer.includes('android-app://')

        if (isStandalone) {
          setInstalled(true)
          // Store installation status
          localStorage.setItem('pwa-installed', 'true')
          return
        }
      }
      setInstalled(false)
    }

    // Check stored installation status
    const storedInstallStatus = localStorage.getItem('pwa-installed')
    if (storedInstallStatus === 'true') {
      setInstalled(true)
    }

    checkInstalled()

    // Add display mode change listener
    const mediaQuery = window.matchMedia('(display-mode: standalone)')
    mediaQuery.addListener(checkInstalled)

    // Improved browser detection
    if ('BeforeInstallPromptEvent' in window) {
      setSupported(true)
      const handleInstallPrompt = (e: Event) => {
        e.preventDefault()
        try {
          const promptEvent = e as BeforeInstallPromptEvent
          setDeferredPrompt(promptEvent)
          setReady(true)
        } catch (error) {
          console.error('Error handling install prompt:', error)
          setReady(false)
        }
      }

      window.addEventListener('beforeinstallprompt', handleInstallPrompt)
      return () => window.removeEventListener('beforeinstallprompt', handleInstallPrompt)
    } else if (
      // Check if use the apple ecosystem
      /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && navigator.userAgent.includes('Macintosh'))
    ) {
      if (
        // This means user is using other browser than safari
        /CriOS/.exec(navigator.userAgent) ||
        /FxiOS/.exec(navigator.userAgent) ||
        /OPT/.exec(navigator.userAgent) ||
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        !!navigator.brave
      ) {
        setNeedSafari(true)
      } else {
        if (window.innerWidth >= 450) {
          setNeedIpadOS(true)
        } else {
          setNeedIOS(true)
        }
      }
    } else {
      setNeedChrome(true)
    }

    // Cleanup function
    return () => {
      mediaQuery.removeListener(checkInstalled)
    }
  }, [])

  useEffect(() => {
    if (supported) {
      if (deferredPrompt !== null && Object.keys(deferredPrompt).length !== 0) {
        setReady(true)
      } else {
        setReady(false)
      }
    }
  }, [supported, deferredPrompt])

  const installApp = async () => {
    try {
      if (!deferredPrompt) return

      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice

      if (outcome === 'accepted') {
        setInstalled(true)
        localStorage.setItem('pwa-installed', 'true')
        location.reload()
      }

      setDeferredPrompt(null)
    } catch (error) {
      console.error('Error installing PWA:', error)
      setReady(false)
    }
  }

  return {
    isInstalled: installed,
    isSupported: supported,
    isReady: ready,
    installApp,
    browserInfo: {
      needSafari,
      needIOS,
      needIpadOS,
      needChrome,
    },
  }
}
